import { Close, History } from '@mui/icons-material';
import { Box } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import { APPROVAL_TYPES, COMITEE_TYPES } from 'lib/api/approvals/types';
import { getRequestEvaluations } from 'lib/api/decisionEngine';
import EnhancedTab, { TabRow } from 'lib/designSystem/TabSimulation';
import { showEntityRecord } from 'lib/functions/utils';
import { Warranty } from 'lib/types/models';
import Comitee from 'pages/Finance/Components/Financing/common/Comittee';
import RiskReviews from 'pages/Finance/Components/Financing/common/Risk';
import React, { useEffect } from 'react';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { ChatWindow } from '../../common/chat';
import { ComiteeTabProvider } from '../../common/Comittee/ComiteeTabProvider';
import File from './File';
import Payments from './Payments';
import Summary from './Summary/index';
import UpdateWarranty from './Update';

interface WarrantyDetailDialogProps {
  open: boolean;
  onClose: () => void;
  warranty: Warranty | null;
  refreshWarranty: () => void;
}

const WarrantyDetailDialog: React.FC<WarrantyDetailDialogProps> = ({
  open,
  onClose,
  warranty,
  refreshWarranty,
}) => {
  if (!warranty) return null;
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const { data: evaluations } = getRequestEvaluations(dispatch, {
    requestId: warranty.id,
    requestType: 'sgr-warranty-request',
  });

  const latestEvaluation = evaluations?.default?.sort(
    (a, b) => b.createdAt - a.createdAt,
  )[0];
  const riskStatus = latestEvaluation?.statusNode.name;

  const getRiskDotColor = (status?: string) => {
    switch (status) {
      case 'Aprobado':
        return 'success.main';
      case 'Rechazado':
        return 'error.main';
      default:
        return 'warning.main';
    }
  };

  const showPayments =
    warranty.status === 'ACTIVE' || warranty.status === 'APPROVED';

  const title = warranty.folioId
    ? `Garantía ${warranty.displayId}`
    : `Cotización ${warranty.id}`;

  const handleOpenEntityRecord = () => {
    showEntityRecord(dispatch, {
      type: 'SGRWarrantyRequest',
      id: warranty.id,
    });
  };

  const handleCloseChat = () => {
    if (!open) onClose();
  };

  const comiteeTabInfo = {
    requestType: APPROVAL_TYPES.WARRANTY_REQUEST,
    comiteeType: COMITEE_TYPES.RISK,
    requestId: warranty.id,
    comiteeId: warranty.comiteeId,
    canEdit: warranty.status === 'IN_REVIEW' || warranty.status === 'REJECTED',
    isRejected: warranty.status === 'REJECTED',
    isActive: warranty.status === 'ACTIVE',
    refreshRequest: async () => refreshWarranty(),
  };

  const tabs = [
    {
      label: 'Resumen',
      component: isEditing ? (
        <UpdateWarranty
          warranty={warranty}
          setIsEditing={setIsEditing}
          refreshWarranty={refreshWarranty}
        />
      ) : (
        <Summary
          warranty={warranty}
          setIsEditing={setIsEditing}
          refreshWarranty={refreshWarranty}
        />
      ),
    },
    showPayments && {
      label: 'Pagos',
      component: <Payments warrantyId={warranty.id} />,
    },
    {
      label: 'Aprobación',
      component: (
        <ComiteeTabProvider info={comiteeTabInfo}>
          <Comitee />
        </ComiteeTabProvider>
      ),
    },
    {
      label: (
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignItems: 'center',
            pr: riskStatus ? 0.5 : 0,
          }}
        >
          <Box component="span">Riesgo</Box>
          {riskStatus && (
            <Box
              component="span"
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: getRiskDotColor(riskStatus),
                position: 'absolute',
                top: -2,
                right: -2,
              }}
            />
          )}
        </Box>
      ),
      component: (
        <RiskReviews item={warranty} requestTypes={['sgr-warranty-request']} />
      ),
    },
    {
      label: 'Documentos',
      component: <File warranty={warranty} />,
      disabled: warranty.status === 'LEAD',
    },
    {
      label: 'Chat',
      component: (
        <Box sx={{ minHeight: 600, overflow: 'auto' }}>
          <ChatWindow
            selectedRequest={warranty}
            requestType="sgrWarrantyRequest"
            setOpenShoppingCart={handleCloseChat}
          />
        </Box>
      ),
    },
  ].filter(Boolean) as TabRow[];

  useEffect(() => {
    if (!open) setIsEditing(false);
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: 'absolute', top: 10, right: 10 }}
      >
        <Close />
      </IconButton>
      <Box style={{ padding: '0 20px' }}>
        <EnhancedTab
          title={title}
          tabs={tabs}
          record={
            warranty.id && (
              <IconButton
                onClick={handleOpenEntityRecord}
                size="small"
                style={{ marginRight: 2 }}
                title="Ver historial de la entidad"
              >
                <History />
              </IconButton>
            )
          }
        />
      </Box>
    </Dialog>
  );
};

export default WarrantyDetailDialog;
